import { useCallback, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  startQuiz,
  setQuizMode,
  updateAnswer,
  removeAnswer,
  updatePercentage,
  markVideoAsSeen,
  nextGroup,
  prevGroup,
  setIntroVideoSeen,
  setAlertQuestionIds,
  setError,
  removeQuestionAlert,
  goBackToStart,
  setTermsAccepted,
  setUploadingFile,
  removeUploadedFile,
  setFileError,
  updateAnswerFile,
  addMessage,
  setSessionId,
  setAllQuestionsAnswered,
  setSessionClosed,
} from "../../store/patient/quizSlice";
import { onLoadRisk } from "../../store/patient/patientSlice";
import {
  setIsLoading,
  setSuccessMessage,
  setErrorMessage,
  clearFormEvent,
} from "../../store/patient/formEventSlice";
import { setQuizAnswered } from "../../store/patient/patientSurgerySlice";
import { sleep, translations } from "../../helpers";
import { gosafeApi, langchainApi, getAnamSessionToken } from "../../api";
import { normalizeShortAnswer } from "../utils/normalizeShortAnswer";
import { CHAT_AUDIO_URLS } from "../data/chatAudioUrls";

const { COMPLETION_MESSAGE, CONSENT_ACCEPTED, CONSENT_REJECTED } =
  CHAT_AUDIO_URLS;

// Traductor de props para medicamentos
const propTranslations = {
  name: "Medicamento",
  full_name: "Nombre completo",
  dosage: "Dosis",
  interval: "Frecuencia",
  treatment_duration: "Duración",
  quantity: "Cantidad",
  administration_route: "Vía",
  usage_indication: "Indicación",
};

const medicationTranslations = {
  name: "Nombre",
  full_name: "Nombre completo",
  quantity: "Cantidad",
  dosage: "Dosis",
  interval: "Frecuencia",
  treatment_duration: "Duración del tratamiento",
  administration_route: "Vía de administración",
  usage_indication: "Indicación",
};

const procedureTranslations = {
  name: "Procedimiento",
  description: "Descripción",
  procedure_type: "Tipo",
  estimated_duration: "Duración estimada",
  anesthesia_type: "Tipo de anestesia",
  recovery_time: "Tiempo de recuperación",
};

export const useQuizStore = () => {
  const dispatch = useDispatch();

  const {
    answers,
    percentage,
    currentGroupIndex,
    videosSeen,
    quizMode,
    isStarted,
    introVideoSeen,
    showQuestions,
    alertQuestionIds,
    error,
    termsAccepted,
    uploadingFiles,
    uploadedFileUrls,
    fileErrors,
    messages,
    sessionId,
    isAllquestionsAnswered,
    isSessionClosed,
  } = useSelector((state) => state.patient.quiz);

  const { id, procedure } = useSelector(
    (state) => state.patient.patientSurgery.patientSurgery.data,
  );

  const { isLoading } = useSelector((state) => state.patient.formEvent);

  // Ref to always hold the latest sessionId for async callbacks (avoids stale closures)
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  const { assessmentQuestionGroups } = procedure.assessment || {};

  // Empezar el quiz
  const startQuizAction = () => {
    dispatch(startQuiz());
  };

  const setQuizModeAction = (mode) => {
    dispatch(setQuizMode(mode));
  };

  const updateAnswerAction = (questionId, value) => {
    dispatch(updateAnswer({ questionId, value }));
  };

  const removeAnswerAction = (questionId) => {
    dispatch(removeAnswer({ questionId }));
  };

  const updatePercentageAction = (percentage) => {
    dispatch(updatePercentage(percentage));
  };

  const markVideoAsSeenAction = (groupIndex, seen = true) => {
    dispatch(markVideoAsSeen({ groupIndex, seen }));
  };

  const nextGroupAction = () => {
    dispatch(nextGroup());
  };

  const prevGroupAction = () => {
    dispatch(prevGroup());
  };

  const setIntroVideoSeenAction = (seen) => {
    dispatch(setIntroVideoSeen(seen));
  };

  const goBackToStartAction = useCallback(() => {
    dispatch(clearFormEvent());
    dispatch(goBackToStart());
  }, [dispatch]);

  const setTermsAcceptedAction = (accepted) => {
    dispatch(setTermsAccepted(accepted));
  };

  const setSessionClosedAction = (closed) => {
    dispatch(setSessionClosed(closed));
  };

  const parseMedications = useCallback(
    (medications) =>
      medications
        .map((med, index) => {
          const parts = [`Medicamento ${index + 1}`];

          Object.entries(med).forEach(([key, value]) => {
            if (value && medicationTranslations[key]) {
              parts.push(`${medicationTranslations[key]}: ${value}`);
            }
          });

          return parts.join("\n");
        })
        .join("\n\n"),
    [],
  );

  const parseProcedures = useCallback(
    (procedures) =>
      procedures
        .map((proc, index) => {
          const parts = [`Procedimiento ${index + 1}`];

          Object.entries(proc).forEach(([key, value]) => {
            if (!value) return;

            if (Array.isArray(value)) {
              parts.push(`${formatLabel(key)}:\n- ${value.join("\n- ")}`);
              return;
            }

            if (procedureTranslations[key]) {
              parts.push(`${procedureTranslations[key]}: ${value}`);
            }
          });

          return parts.join("\n");
        })
        .join("\n\n"),
    [],
  );

  const extractMedications = useCallback(
    async (questionId, fileUrl, titleQuestion) => {
      try {
        const { title } = titleQuestion;
        const containMedication = title.toLowerCase().includes("medicamento");
        let response = null;
        if (containMedication) {
          response = await langchainApi.post("/medications/extract", {
            aws_url: fileUrl,
          });
        } else {
          response = await langchainApi.post("/procedures/extract-metadata", {
            aws_url: fileUrl,
          });
        }

        const { data } = response;

        const medications = data?.medications || data?.procedures || [];
        // Convertir medicamentos a texto plano
        const medicationsText = containMedication
          ? parseMedications(medications)
          : parseProcedures(medications);

        // Actualizar la respuesta de la pregunta con los medicamentos extraídos
        if (medicationsText) {
          dispatch(updateAnswer({ questionId, value: medicationsText }));
        }

        return;
      } catch (langchainError) {
        // console.error("Error calling langchain API:", langchainError);
        throw langchainError;
      }
    },
    [dispatch, parseMedications, parseProcedures],
  );

  const formatLabel = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const uploadFile = useCallback(
    async (questionId, file) => {
      try {
        if (!file) return;

        dispatch(setUploadingFile({ questionId, uploading: true }));

        // Eliminar archivos existentes antes de subir el nuevo
        const existingFiles = uploadingFiles[questionId]?.files || [];
        if (existingFiles.length > 0) {
          const urlsToDelete = existingFiles.map((file) => file.url);
          await gosafeApi.delete(`/fileUploader`, {
            data: { urls: urlsToDelete },
          });
        }

        const body = {
          patientSurgeryId: id,
          questionId,
        };

        const formData = new FormData();
        formData.append("file", file);
        formData.append("body", JSON.stringify(body));

        const { data } = await gosafeApi.post("/fileUploader", formData);

        console.log("data", data);
        const { files, titleQuestion } = data;

        // Extraer medicamentos del archivo subido
        await extractMedications(questionId, files[0].url, titleQuestion);

        dispatch(
          updateAnswerFile({
            questionId,
            ...data,
          }),
        );
        return;
      } catch (error) {
        const errorMessage =
          translations[error?.response?.data?.message] ||
          error?.response?.data?.message ||
          error?.message;

        dispatch(setFileError({ questionId, message: errorMessage }));
        return;
      }
    },
    [dispatch, id, translations, uploadingFiles, extractMedications],
  );

  const deleteFile = useCallback(
    async (url, questionId) => {
      try {
        dispatch(setUploadingFile({ questionId, uploading: true }));

        const body = {
          urls: [url],
        };

        await gosafeApi.delete(`/fileUploader`, {
          data: body,
        });

        // Limpiar la respuesta de la pregunta cuando se elimina el archivo
        dispatch(updateAnswer({ questionId, value: "" }));
        dispatch(removeUploadedFile({ questionId }));
        return;
      } catch (error) {
        const errorMessage =
          translations[error?.response?.data?.message] ||
          error?.response?.data?.message ||
          error?.message;

        dispatch(setFileError({ questionId, message: errorMessage }));
        return;
      }
    },
    [dispatch],
  );

  const handleAnswerChange = useCallback(
    (questionId, value) => {
      if (alertQuestionIds.includes(questionId)) {
        dispatch(removeQuestionAlert(questionId));
      }

      const question = assessmentQuestionGroups
        ?.flatMap((group) => group.questionGroup.questions)
        .find((q) => q.id === questionId);

      dispatch(updateAnswer({ questionId, value }));

      if (
        question?.requiresFollowUp &&
        value !== "Si" &&
        value !== "Sí" &&
        question.followUpQuestion
      ) {
        dispatch(removeAnswer({ questionId: question.followUpQuestion.id }));
      }
    },
    [dispatch, assessmentQuestionGroups, alertQuestionIds],
  );

  const calculatePercentage = useCallback(() => {
    const allQuestions =
      assessmentQuestionGroups?.flatMap(
        (group) => group.questionGroup?.questions || [],
      ) || [];

    let totalRequiredQuestions = allQuestions.length;
    let answeredQuestions = 0;

    allQuestions.forEach((question) => {
      const answer = answers.find((a) => a.questionId === question.id);

      if (answer && answer.value.trim() !== "") {
        answeredQuestions += 1;
      }

      if (
        question.requiresFollowUp &&
        (answer?.value === "Si" || answer?.value === "Sí") &&
        question.followUpQuestion
      ) {
        totalRequiredQuestions += 1;
        const followUpAnswer = answers.find(
          (a) => a.questionId === question.followUpQuestion.id,
        );
        if (followUpAnswer && followUpAnswer.value.trim() !== "") {
          answeredQuestions += 1;
        }
      }
    });

    const newPercentage =
      totalRequiredQuestions === 0
        ? 0
        : Math.round((answeredQuestions / totalRequiredQuestions) * 100);
    dispatch(updatePercentage(newPercentage));
  }, [answers, assessmentQuestionGroups, dispatch]);

  const calculateUnansweredQuestions = useCallback(() => {
    const allQuestions =
      assessmentQuestionGroups?.flatMap(
        (group) => group.questionGroup?.questions || [],
      ) || [];

    console.log('[QUIZ STORE] calculateUnansweredQuestions - Total questions:', allQuestions.length);
    console.log('[QUIZ STORE] calculateUnansweredQuestions - Current answers:', answers?.length || 0, answers);

    const unanswered = [];

    allQuestions.forEach((question) => {
      const answer = answers.find((a) => a.questionId === question.id);

      if (!answer || answer.value.trim() === "") {
        console.log('[QUIZ STORE] Question unanswered:', question.id, question.title);
        unanswered.push(question);
      } else if (
        question.requiresFollowUp &&
        (answer.value === "Si" || answer.value === "Sí") &&
        question.followUpQuestion
      ) {
        const followUpAnswer = answers.find(
          (a) => a.questionId === question.followUpQuestion.id,
        );

        if (!followUpAnswer || followUpAnswer.value.trim() === "") {
          console.log('[QUIZ STORE] Follow-up question unanswered:', question.followUpQuestion.id);
          unanswered.push(question.followUpQuestion);
        }
      } else {
        console.log('[QUIZ STORE] Question answered:', question.id, 'Answer:', answer.value);
      }
    });

    console.log('[QUIZ STORE] Unanswered count:', unanswered.length);
    return unanswered;
  }, [assessmentQuestionGroups, answers]);

  const validateAllQuestions = useCallback(() => {
    const unanswered = calculateUnansweredQuestions();
    // console.log("unanswered", unanswered);

    if (unanswered.length === 0) {
      if (error !== null) dispatch(setError(null));
      return;
    }
  }, [dispatch, calculateUnansweredQuestions, error]);

  const sendQuizAnswers = useCallback(async () => {
    try {
      dispatch(setIsLoading(true));

      const body = {
        patientSurgeryId: id,
        responses: answers,
      };

      // console.log("body", body);

      const { data } = await gosafeApi.post("strat", body);

      dispatch(onLoadRisk(data));
      dispatch(setQuizAnswered(data));
      dispatch(setSuccessMessage("Encuesta enviada exitosamente"));
      return data;
    } catch (error) {
      const errorMessage =
        translations[error?.response?.data?.message] ||
        error?.response?.data?.message ||
        error?.message;

      dispatch(setErrorMessage(errorMessage));
      return;
    }
  }, [dispatch, id, answers]);

  const handleSubmitAnswers = useCallback(
    async (setFormSubmitted) => {
      try {
        if (!termsAccepted) {
          dispatch(setError("Debes aceptar los terminos y condiciones"));
          return;
        }

        const unanswered = calculateUnansweredQuestions();
        // console.log("unanswered", unanswered);

        if (unanswered.length > 0) {
          dispatch(setAlertQuestionIds(unanswered.map((q) => q.id)));
          dispatch(setError("Debes responder todas las preguntas"));

          const firstUnansweredId = unanswered[0].id;
          const element = document.querySelector(
            `[name="question-${firstUnansweredId}"]`,
          );
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }

          return;
        }

        setFormSubmitted(true);
        sendQuizAnswers();
      } catch (error) {
        dispatch(setError(`Error in handleSubmitAnswers: ${error.message}`));
      }
    },
    [dispatch, calculateUnansweredQuestions, sendQuizAnswers, termsAccepted],
  );

  const initializeChatBot = useCallback(async () => {
    try {
      dispatch(setIsLoading());

      const body = {
        message: null,
        conversation_id: null,
      };

      const { data } = await langchainApi.post("/conversation/message", body);

      // console.log("data", data);

      dispatch(setSessionId(data.conversation_id));

      const botMessage = {
        type: "bot",
        content: data.response,
        timestamp: new Date().toLocaleTimeString(),
        audioUrl: data.audioUrl,
        // questionId: data.current_question_id,
      };

      dispatch(addMessage(botMessage));
      dispatch(setSuccessMessage("Conversacion iniciada"));
      return;
    } catch (error) {
      dispatch(setErrorMessage(`Error in initializeChatBot: ${error.message}`));
      return;
    }
  }, [dispatch]);

  const handleDataProtectionResponse = useCallback(
    async (text) => {
      const userMessage = {
        type: "user",
        content: text,
        timestamp: new Date().toLocaleTimeString(),
      };
      dispatch(addMessage(userMessage));

      await sleep(1000);

      if (
        text.toLowerCase().includes("sí") ||
        text.toLowerCase().includes("si")
      ) {
        dispatch(setTermsAccepted(true));
        const finalMessage = {
          type: "bot",
          content:
            "Perfecto. Consentimiento registrado. Tu encuesta será enviada automáticamente.",
          timestamp: new Date().toLocaleTimeString(),
          shouldSubmit: true,
          audioUrl: CONSENT_ACCEPTED,
        };
        dispatch(addMessage(finalMessage));
      } else {
        const rejectionMessage = {
          type: "bot",
          content:
            "Debes aceptar el consentimiento para continuar. ¿Aceptas el tratamiento de tus datos? (Responde 'Sí')",
          timestamp: new Date().toLocaleTimeString(),
          audioUrl: CONSENT_REJECTED,
        };
        dispatch(addMessage(rejectionMessage));
      }
    },
    [dispatch],
  );

  const sendUserMessage = useCallback(
    async (text) => {
      try {
        if (!text.trim() || isLoading) return;

        const normalizedText = normalizeShortAnswer(text);

        // Handle data protection response if quiz is completed
        if (isAllquestionsAnswered) {
          handleDataProtectionResponse(normalizedText);
          return;
        }

        const userMessage = {
          type: "user",
          content: text,
          timestamp: new Date().toLocaleTimeString(),
        };

        dispatch(addMessage(userMessage));
        dispatch(setIsLoading());

        console.log('[LANGCHAIN] Sending user message:', normalizedText);
        const { data } = await langchainApi.post("/conversation/message", {
          conversation_id: sessionId,
          message: normalizedText,
        });

        // console.log("data", data);

        // Check if quiz is completed
        if (data.context?.is_complete) {
          const completionMessage = {
            type: "bot",
            content: `¡Encuesta completada! Ahora necesito tu consentimiento para el tratamiento de datos.
            Entiendes cómo se usarán tus datos y das el consentimiento para el tratamiento de mi información personal y de salud. ¿Aceptas? (Responde 'Sí' para continuar)`,
            timestamp: new Date().toLocaleTimeString(),
            audioUrl: COMPLETION_MESSAGE,
          };
          dispatch(addMessage(completionMessage));
          dispatch(setAllQuestionsAnswered(true));
        } else {
          const botMessage = {
            type: "bot",
            content: data.response,
            timestamp: new Date().toLocaleTimeString(),
            context: data.context,
            audioUrl: data.audioUrl,
            mode: data.mode,
          };
          dispatch(addMessage(botMessage));
        }

        if (
          data.context?.interpreted_answer &&
          data.context?.answered_question_id
        ) {
          dispatch(
            updateAnswer({
              questionId: data.context.answered_question_id,
              value: data.context.interpreted_answer,
            }),
          );
        }

        dispatch(setSuccessMessage("Mensaje respondido"));
        return;
      } catch (error) {
        dispatch(setErrorMessage(`Error in sendUserMessage: ${error.message}`));
        return;
      }
    },
    [
      dispatch,
      handleDataProtectionResponse,
      isLoading,
      sessionId,
      isAllquestionsAnswered,
    ],
  );

  const uploadFileForChatbot = useCallback(
    async (questionId, file) => {
      try {
        if (!file) return;

        dispatch(setUploadingFile({ questionId, uploading: true }));

        const body = {
          patientSurgeryId: id,
          questionId,
        };

        const formData = new FormData();
        formData.append("file", file);
        formData.append("body", JSON.stringify(body));

        const { data } = await gosafeApi.post("/fileUploader", formData);

        // Extraer medicamentos del archivo
        const medicationsResponse = await langchainApi.post(
          "/medications/extract",
          {
            aws_url: data.files[0].url,
          },
        );

        const medications = medicationsResponse.data.medications || [];

        // Convertir medicamentos a texto plano
        const medicationsText = medications
          .map((med) => {
            const parts = [];

            Object.entries(med).forEach(([key, value]) => {
              if (value && propTranslations[key]) {
                parts.push(`${propTranslations[key]}: ${value}`);
              }
            });

            return parts.join(" - ");
          })
          .join("\n\n");

        // Enviar medicamentos como mensaje de usuario
        if (medicationsText) {
          await sendUserMessage(medicationsText);
        }

        dispatch(
          updateAnswerFile({
            questionId,
            ...data,
          }),
        );
        return;
      } catch (error) {
        const errorMessage =
          translations[error?.response?.data?.message] ||
          error?.response?.data?.message ||
          error?.message;

        dispatch(setFileError({ questionId, message: errorMessage }));
        return;
      }
    },
    [dispatch, id, translations, sendUserMessage],
  );

  // ANAM-specific methods
  const initializeAnam = useCallback(async () => {
    try {
      dispatch(setIsLoading());

      // Get Anam session token
      const anamTokenResponse = await getAnamSessionToken();
      const sessionToken =
        anamTokenResponse.sessionToken || anamTokenResponse.session_token;

      if (!sessionToken) {
        throw new Error("No session token received from backend");
      }

      // NOTE: Do NOT dispatch(setSessionId(sessionToken)) here.
      // sessionId in Redux must remain the LangChain conversation_id (set by initializeChatBot).
      // The Anam streaming token is returned to the caller and stored separately.
      dispatch(setSuccessMessage("GoSafe Avatar inicializado"));
      return sessionToken;
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Error al inicializar GoSafe Avatar";
      dispatch(setErrorMessage(errorMessage));
      throw error;
    }
  }, [dispatch]);

  const processAnamAnswer = useCallback(
    async (userTranscript) => {
      try {
        if (!userTranscript || !userTranscript.trim()) return;

        dispatch(setIsLoading());

        const normalizedTranscript = normalizeShortAnswer(userTranscript);

        // Use sessionIdRef to always get the latest conversation_id (avoids stale closure)
        const currentSessionId = sessionIdRef.current;

        // Send user transcript to LangChain API to get interpreted answer
        console.log('[LANGCHAIN] Sending user message (Anam):', normalizedTranscript);
        const { data } = await langchainApi.post("/conversation/message", {
          conversation_id: currentSessionId,
          message: normalizedTranscript,
        });


        if (
          data.context?.interpreted_answer &&
          data.context?.answered_question_id
        ) {
          console.log('[QUIZ STORE] Updating answer:', {
            questionId: data.context.answered_question_id,
            value: data.context.interpreted_answer
          });
          dispatch(
            updateAnswer({
              questionId: data.context.answered_question_id,
              value: data.context.interpreted_answer,
            }),
          );
        } else {
          console.warn('[QUIZ STORE] No interpreted_answer or answered_question_id in response:', data.context);
        }

        dispatch(setSuccessMessage("Respuesta procesada"));
        return data;
      } catch (error) {
        dispatch(
          setErrorMessage(`Error procesando respuesta: ${error.message}`),
        );
        throw error;
      }
    },
    [dispatch],
  );

  // Request the next assessment question from LangChain (for Anam Avatar dynamic flow)
  const getNextAnamQuestion = useCallback(
    async (message = null) => {
      try {
        // Use sessionIdRef to always get the latest conversation_id (avoids stale closure)
        const currentSessionId = sessionIdRef.current;

        const { data } = await langchainApi.post("/conversation/message", {
          conversation_id: currentSessionId,
          message,
        });

        // Process any answer data returned
        if (
          data.context?.interpreted_answer &&
          data.context?.answered_question_id
        ) {
          dispatch(
            updateAnswer({
              questionId: data.context.answered_question_id,
              value: data.context.interpreted_answer,
            }),
          );
        }

        return data;
      } catch (error) {
        console.error('[QUIZ STORE] Error getting next Anam question:', error);
        throw error;
      }
    },
    [dispatch],
  );

  return {
    // Properties
    answers,
    percentage,
    currentGroupIndex,
    videosSeen,
    quizMode,
    isStarted,
    introVideoSeen,
    showQuestions,
    alertQuestionIds,
    error,
    termsAccepted,
    uploadingFiles,
    uploadedFileUrls,
    fileErrors,
    messages,
    sessionId,
    isAllquestionsAnswered,
    isSessionClosed,

    // Methods
    startQuiz: startQuizAction,
    setQuizMode: setQuizModeAction,
    updateAnswer: updateAnswerAction,
    removeAnswer: removeAnswerAction,
    updatePercentage: updatePercentageAction,
    markVideoAsSeen: markVideoAsSeenAction,
    nextGroup: nextGroupAction,
    prevGroup: prevGroupAction,
    setIntroVideoSeen: setIntroVideoSeenAction,
    goBackToStart: goBackToStartAction,
    setTermsAccepted: setTermsAcceptedAction,
    setSessionClosed: setSessionClosedAction,
    validateAllQuestions,
    calculatePercentage,
    calculateUnansweredQuestions,
    handleAnswerChange,
    uploadFile,
    deleteFile,
    sendQuizAnswers,
    handleSubmitAnswers,
    initializeChatBot,
    sendUserMessage,
    uploadFileForChatbot,
    initializeAnam,
    processAnamAnswer,
    getNextAnamQuestion,
  };
};
